import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import MonthNav from '@/components/feature/MonthNav';
import { useStore } from '@/hooks/useStore';
import type { Despesa } from '@/mocks/store';

function fmt(v: number) { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }
const CATS = ['Aluguel', 'Utilidades', 'Marketing', 'Materiais', 'Logística', 'Pessoal', 'Outros'];

function RetiradaProdCard({ descricao, valor }: { descricao: string; valor: number }) {
  const qtdMatch = descricao.match(/\((\d+)x\)/);
  const qtd = qtdMatch ? qtdMatch[1] : '1';
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="bg-dark-card2 rounded-lg p-2 text-center">
        <p className="text-[9px] text-dark-muted uppercase mb-0.5">Quantidade</p>
        <p className="text-xs font-bold font-mono text-dark-text">{qtd}x</p>
      </div>
      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-2 text-center">
        <p className="text-[9px] text-dark-muted uppercase mb-0.5">Custo debitado</p>
        <p className="text-xs font-bold font-mono text-orange-400">{fmt(valor)}</p>
      </div>
    </div>
  );
}

export default function DespesasPage() {
  const { vendas, despesas, retiradaProdutos, addDespesa, updateDespesa, deleteDespesa, totalDespesas } = useStore();
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Despesa | null>(null);
  const [form, setForm] = useState({ descricao: '', valor: '', categoria: 'Aluguel', data: new Date().toISOString().split('T')[0] });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());

  const mesPad = String(mes).padStart(2, '0');
  const anoStr = String(ano);

  const despesasFiltradas = despesas.filter(d => {
    if (!d.data) return true;
    const [a, m] = d.data.split('-');
    return a === anoStr && m === mesPad;
  });
  const totalFiltrado = despesasFiltradas.reduce((acc, d) => acc + d.valor, 0);

  // Calcula quebra do card Total Despesas
  const taxasCartaoMes = vendas
    .filter(v => v.data?.startsWith(`${anoStr}-${mesPad}`))
    .reduce((acc, v) => acc + (v.valorTotal - v.valorLiquido - (v.valorDesconto ?? 0)), 0);
  const descontosMes = vendas
    .filter(v => v.data?.startsWith(`${anoStr}-${mesPad}`))
    .reduce((acc, v) => acc + (v.valorDesconto ?? 0), 0);
  const retiradasDespMes = despesasFiltradas
    .filter(d => d.categoria === 'Retirada de Produto')
    .reduce((acc, d) => acc + d.valor, 0);

  function openNew() { setEditingItem(null); setForm({ descricao: '', valor: '', categoria: 'Aluguel', data: new Date().toISOString().split('T')[0] }); setShowModal(true); }
  function openEdit(d: Despesa) { setEditingItem(d); setForm({ descricao: d.descricao, valor: String(d.valor), categoria: d.categoria, data: d.data }); setShowModal(true); }

  function handleSave() {
    if (!form.descricao || !form.valor) return;
    if (editingItem) {
      updateDespesa(editingItem.id, { descricao: form.descricao, valor: Number(form.valor), categoria: form.categoria, data: form.data });
    } else {
      addDespesa({ descricao: form.descricao, valor: Number(form.valor), categoria: form.categoria, data: form.data });
    }
    setShowModal(false);
    setEditingItem(null);
    setForm({ descricao: '', valor: '', categoria: 'Aluguel', data: new Date().toISOString().split('T')[0] });
  }

  return (
    <AppLayout>
      <TopBar title="Despesas" subtitle="Controle seus gastos operacionais" />
      <ActionBar>
        <MonthNav mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); }} />
        <button onClick={openNew} className="btn-gold w-full flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap">
          <i className="ri-add-line text-base"></i> Nova Despesa
        </button>
      </ActionBar>
      <div className="pt-[200px] md:pt-[200px] p-3 md:p-6 space-y-3 animate-fade-in-up">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4">
            <div className="flex flex-col items-center text-center mb-3">
              <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Total Despesas</p>
              <p className="text-lg md:text-2xl font-bold font-mono text-red-400">{fmt(totalFiltrado)}</p>
            </div>
            <div className="space-y-1.5 border-t border-dark-border pt-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-dark-muted flex items-center gap-1"><i className="ri-bank-card-line text-orange-400"></i> Taxas cartão</span>
                <span className="text-xs font-mono text-orange-400">{fmt(taxasCartaoMes)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-dark-muted flex items-center gap-1"><i className="ri-percent-line text-red-400"></i> Descontos</span>
                <span className="text-xs font-mono text-red-400">{fmt(descontosMes)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-dark-muted flex items-center gap-1"><i className="ri-shirt-line text-yellow-400"></i> Retiradas produto</span>
                <span className="text-xs font-mono text-yellow-400">{fmt(retiradasDespMes)}</span>
              </div>
            </div>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Lançamentos</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-dark-text">{despesasFiltradas.length}</p>
          </div>
        </div>
        <div className="space-y-2">
          {despesasFiltradas.length === 0 ? (
            <div className="bg-dark-card border border-dark-border rounded-xl p-8 flex flex-col items-center text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card2 mb-2"><i className="ri-bill-line text-xl text-dark-muted"></i></div>
              <p className="text-sm text-dark-muted">Nenhuma despesa neste mês</p>
            </div>
          ) : despesasFiltradas.map(d => {
            const isRetiradaProd = d.categoria === 'Retirada de Produto';
            return (
              <div key={d.id} className={`bg-dark-card border rounded-xl p-4 ${isRetiradaProd ? 'border-orange-500/25' : 'border-dark-border'}`}>
                {/* Cabeçalho */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-dark-text text-sm truncate">
                      {isRetiradaProd
                        ? d.descricao.replace(/^Retirada:\s*/i, '').replace(/\s*—.*$/, '')
                        : d.descricao}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isRetiradaProd ? 'bg-orange-500/10 text-orange-400' : 'bg-dark-card2 text-dark-muted'}`}>
                        {isRetiradaProd && <i className="ri-shirt-line mr-1"></i>}
                        {d.categoria}
                      </span>
                      <span className="text-[10px] text-dark-muted">{new Date(d.data).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2 shrink-0">
                    {!isRetiradaProd && (
                      <button onClick={() => openEdit(d)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-dark-card2 text-dark-muted hover:text-neon cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                    )}
                    <button onClick={() => setConfirmDelete(d.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-dark-card2 text-dark-muted hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                  </div>
                </div>
                {/* Grid de detalhes */}
                {isRetiradaProd
                  ? <RetiradaProdCard descricao={d.descricao} valor={d.valor} />
                  : (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-dark-card2 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-dark-muted uppercase mb-0.5">Categoria</p>
                        <p className="text-xs font-bold text-dark-text truncate">{d.categoria}</p>
                      </div>
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-center">
                        <p className="text-[9px] text-dark-muted uppercase mb-0.5">Valor</p>
                        <p className="text-xs font-bold font-mono text-red-400">{fmt(d.valor)}</p>
                      </div>
                    </div>
                  )
                }
              </div>
            );
          })}
        </div>
      </div>
      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editingItem ? 'Editar Despesa' : 'Nova Despesa'}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Descrição</label><input className="input-dark" placeholder="Ex: Aluguel da loja" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-dark-muted mb-1 block font-medium">Valor (R$)</label><input type="number" className="input-dark" placeholder="0,00" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} /></div>
                <div><label className="text-xs text-dark-muted mb-1 block font-medium">Categoria</label><select className="input-dark" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>{CATS.map(c => <option key={c}>{c}</option>)}</select></div>
              </div>
              <div><label className="text-xs text-dark-muted mb-1 block font-medium">Data</label><input type="date" className="input-dark" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">{editingItem ? 'Salvar' : 'Registrar'}</button>
            </div>
          </div>
        </div>
      )}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <h3 className="text-center text-base font-bold mb-2 text-dark-text">Excluir despesa?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteDespesa(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
